import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faTrash as faTrashSolid } from '@fortawesome/pro-solid-svg-icons';
import { NotificationType } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { NotificationSubscriptionGroup } from 'custom_hooks/data_hooks/data_source/groupedNotificationSubscriptionsDataSource';
import { getAllHardwareNotificationSubscriptions } from 'custom_hooks/data_hooks/data_source/hardwareNotificationSubscriptionsDataSource';
import { getAllNotificationSubscriptions } from 'custom_hooks/data_hooks/data_source/notificationSubscriptionsDataSource';
import { useState } from 'react';
import { tokens } from 'styles/tokens';
import { drainQuery } from 'utils/query_v3';
import { capitalizeEachWord } from 'utils/string';

const getNotificationType = (nt: NotificationType) => {
    switch (nt) {
        case NotificationType.Hardware:
            return 'hardware';
        case NotificationType.Indicator:
            return 'indicator alerts';
        case NotificationType.MachineHealth:
            return 'machine health issues';
        case NotificationType.HardwareHealth:
            return 'hardware issues';

        default:
            throw new Error('invalid notification type');
    }
};

type GroupedNotificationSubscriptionsDeleteButtonProps = {
    groupedSubscription: NotificationSubscriptionGroup;
    onDelete: () => void;
};

export function GroupedNotificationSubscriptionsDeleteButton({
    groupedSubscription,
    onDelete,
}: GroupedNotificationSubscriptionsDeleteButtonProps) {
    const { showToast } = useNotificationContext();
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

    const notificationType = getNotificationType(
        groupedSubscription.discriminator as NotificationType,
    );

    const {
        mutate: deleteNotificationSubscriptionGroup,
        isPending: isDeleting,
    } = useMutation({
        meta: { name: 'delete-notification-subscription-group' },
        mutationFn: async () => {
            // todo @jp: pull out the shared stuff. and use 1 endpoint
            switch (groupedSubscription.discriminator) {
                case NotificationType.Hardware: {
                    const filter = {
                        locationId: groupedSubscription.Location.LocationId,
                        isSubscriptionGroupMode: true,
                    };
                    const subscriptions = await drainQuery(
                        getAllHardwareNotificationSubscriptions,
                        { filter, pageLimit: 200 },
                    );

                    await sdClient.deleteManyHardwareNotificationSubscriptions({
                        body: subscriptions
                            .flatMap((page) => page.results)
                            .map((subscription) => subscription.Id),
                    });
                    return;
                }
                case NotificationType.Indicator: {
                    const filter = {
                        groupId: groupedSubscription.Group?.GroupId,
                        locationId: groupedSubscription.Location.LocationId,
                        isSubscriptionGroupMode: true,
                        notificationType: NotificationType.Indicator,
                    };
                    const subscriptions = await drainQuery(
                        getAllNotificationSubscriptions,
                        { filter, pageLimit: 200 },
                    );

                    await sdClient.deleteManyIndicatorNotificationSubscriptions({
                        body: subscriptions
                            .flatMap((page) => page.results)
                            .map((subscription) => subscription.Id),
                    });
                    return;
                }
                case NotificationType.MachineHealth: {
                    const filter = {
                        groupId: groupedSubscription.Group?.GroupId,
                        locationId: groupedSubscription.Location.LocationId,
                        isSubscriptionGroupMode: true,
                        notificationType: NotificationType.MachineHealth,
                    };
                    const subscriptions = await drainQuery(
                        getAllNotificationSubscriptions,
                        { filter, pageLimit: 200 },
                    );

                    await sdClient.deleteManyMachineHealthNotificationSubscriptions({
                        body: subscriptions
                            .flatMap((page) => page.results)
                            .map((subscription) => subscription.Id),
                    });
                    return;
                }
                case NotificationType.HardwareHealth: {
                    const filter = {
                        locationId: groupedSubscription.Location.LocationId,
                        isSubscriptionGroupMode: true,
                        notificationType: NotificationType.HardwareHealth,
                    };
                    const subscriptions = await drainQuery(
                        getAllNotificationSubscriptions,
                        { filter, pageLimit: 200 },
                    );

                    await sdClient.deleteManyHardwareNotificationSubscriptions({
                        body: subscriptions
                            .flatMap((page) => page.results)
                            .map((subscription) => subscription.Id),
                    });
                    return;
                }
                default:
                    throw new Error('invalid notification type');
            }
        },

        onSuccess: () => {
            setConfirmationModalOpen(false);
            onDelete();
            showToast({
                content: `Successfully deleted ${notificationType} notification subscriptions group`,
                type: 'success',
            });
        },

        onError: () => {
            showToast({
                content: `Failed to delete ${notificationType} notification subscriptions group`,
                type: 'error',
            });
        },
    });

    return (
        <>
            <HoverIconButton
                title={`Delete ${notificationType} notification subscriptions group`}
                icon={faTrash}
                hoverIcon={faTrashSolid}
                color={tokens.text.secondary}
                hoverColor={tokens.error.default}
                onClick={() => setConfirmationModalOpen(true)}
                disabled={isDeleting}
            />
            {confirmationModalOpen && (
                <ConfirmModal
                    title={`Delete ${capitalizeEachWord(
                        notificationType,
                    )} Notification Subscription Group`}
                    actionText={`delete this ${notificationType} notification subscriptions group? Other users in your organization may be referencing it.`}
                    checkText={`delete this ${notificationType} notification subscriptions group`}
                    actionButtonText="Delete"
                    closable={!isDeleting}
                    actionable={!isDeleting}
                    onAction={deleteNotificationSubscriptionGroup}
                    onClose={() => setConfirmationModalOpen(false)}
                />
            )}
        </>
    );
}
